import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faThumbtack, faXmark } from '@fortawesome/free-solid-svg-icons';
import { WidgetBase } from '@/core/widgets/export';
import {
  CONTENT_WIDTH,
  SidePanelId,
  SidePanelUiState,
  sidePanelShareUrl,
} from '@/core/deep-dive/contexts/side-panel-provider';
import CopyLinkButton from '@/core/deep-dive/components/copy-link-button';
import { Permissions } from '@/core/permissions/export';
import WidgetPane from '@/core/deep-dive/components/widget-pane';
import { getDeepDiveApp, isDeepDiveAppVisible } from '@/core/deep-dive/services/app-registry';
import { useSidePanelTabs } from '@/core/deep-dive/hooks/use-side-panel-tabs';
import { useAppPin } from '@/core/deep-dive/components/use-app-pin';
import { useAppHeaderActions } from '@/core/deep-dive/contexts/app-header-actions-context';
import styles from './side-panel.module.css';

export interface SidePanelTab {
  id: SidePanelId;
  widget: WidgetBase;
}

function SidePanelHeaderActions({
  appId,
  sidePanelUi,
  canBrowseDeepDive,
}: {
  appId: string;
  sidePanelUi: SidePanelUiState;
  canBrowseDeepDive: boolean;
}) {
  const { isPinned, togglePin } = useAppPin(appId);
  const appActions = useAppHeaderActions();

  const copyLink = (
    <CopyLinkButton
      href={sidePanelShareUrl(
        window.location,
        sidePanelUi.selectedAppId,
        sidePanelUi.widgetPath,
        sidePanelUi.isRailVisible,
      )}
    />
  );

  return (
    <div className={styles.headerActions}>
      {appActions ?? copyLink}
      {canBrowseDeepDive && (
        <button
          type={'button'}
          className={`btn btn-sm ${isPinned ? '' : 'text-muted'}`}
          style={isPinned ? { color: 'var(--bs-tertiary-400)' } : undefined}
          onClick={togglePin}
          title={isPinned ? 'Unpin' : 'Pin'}
        >
          <FontAwesomeIcon icon={faThumbtack} />
        </button>
      )}
      <button
        type={'button'}
        className={'btn btn-sm text-muted'}
        onClick={() => sidePanelUi.closePanel('deep-dive')}
        title={'Close'}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}

export default function SidePanel({ tabs }: { tabs: SidePanelTab[] }) {
  const {
    sidePanelUi,
    permissions,
    isOpen,
    isRailBrowseActive,
    isAppActive,
    toggleRail,
    toggleApp,
  } = useSidePanelTabs();

  const canBrowseDeepDive = permissions.includes(Permissions.DEEP_DIVE_ACCESS);

  const lastPanelIdRef = useRef<SidePanelId | null>(null);
  if (sidePanelUi.openPanelId) lastPanelIdRef.current = sidePanelUi.openPanelId;
  const displayPanelId = sidePanelUi.openPanelId ?? lastPanelIdRef.current;

  const panelWidth = CONTENT_WIDTH;

  // The "Widgets" tab's own header only makes sense while browsing the rail —
  // once a specific app is open (rail hidden), show that app's own identity
  // instead of the generic tab title.
  const contentHeader = (tab: SidePanelTab) => {
    if (tab.id === 'deep-dive' && !sidePanelUi.isRailVisible && sidePanelUi.selectedAppId) {
      const app = getDeepDiveApp(sidePanelUi.selectedAppId);
      if (app) {
        return (
          <>
            {app.icon} {app.label ?? app.defaultTitle}
          </>
        );
      }
    }
    return tab.widget.headerPanel;
  };

  return (
    <>
      {isOpen && (
        <div
          className={styles.backdrop}
          onClick={() => sidePanelUi.openPanelId && sidePanelUi.closePanel(sidePanelUi.openPanelId)}
        />
      )}
      <div
        className={styles.content}
        style={{
          width: panelWidth,
          transform: isOpen ? 'translateX(0)' : `translateX(${panelWidth}px)`,
        }}
      >
        {tabs.map(tab => {
          const active = tab.id === displayPanelId;
          return (
            <div
              key={tab.id}
              className={active ? undefined : styles.contentPaneHidden}
              style={{
                display: active ? 'flex' : undefined,
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <div className={styles.contentHeader}>
                <span>{contentHeader(tab)}</span>
                {tab.id === 'deep-dive' && sidePanelUi.selectedAppId && (
                  <SidePanelHeaderActions
                    appId={sidePanelUi.selectedAppId}
                    sidePanelUi={sidePanelUi}
                    canBrowseDeepDive={canBrowseDeepDive}
                  />
                )}
              </div>
              <div className={styles.contentBody}>
                <WidgetPane widget={tab.widget} panelId={tab.id} />
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={styles.iconColumn}
        style={{ transform: isOpen ? `translateX(-${panelWidth}px)` : undefined }}
      >
        {canBrowseDeepDive && (
          <button
            type={'button'}
            className={`${styles.tab} ${isRailBrowseActive ? styles.tabActive : ''}`}
            onClick={toggleRail}
            title={'Widgets'}
          >
            <FontAwesomeIcon icon={faCircleInfo} size="sm" />
          </button>
        )}
        {sidePanelUi.pinnedAppIds.map(id => {
          const app = getDeepDiveApp(id);
          if (!app || !isDeepDiveAppVisible(app, permissions)) return null;
          const isActive = isAppActive(id);
          return (
            <button
              type={'button'}
              key={id}
              className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
              onClick={() => toggleApp(id)}
              title={app.label ?? app.defaultTitle}
            >
              {app.icon}
            </button>
          );
        })}
      </div>
    </>
  );
}
